import { ArrowDownTrayIcon, ArrowUpTrayIcon, BuildingOffice2Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { formatDateWithTime } from '../../../../common-utils/formatters';
import type { Company, SoftwareType } from '../../../@types/Company';
import { SOFTWARE_CONFIGS } from '../../../@types/Company';
import type { RootState } from '../../../store/store';
import {
  downloadCompanyConfigExport,
  formatUploadAge,
  getSoftwareDisplayName,
  isCoproValidated,
} from '../../../utils/configExport';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';

interface PaginationData {
  pagination: {
    currentPage: number;
    totalPages: number;
    totalResults: number;
    itemsPerPage: number;
  };
  results: Company[];
}

// Software types that have import implemented — derived from shared config
const IMPLEMENTED_SOFTWARE: SoftwareType[] = SOFTWARE_CONFIGS.filter((c) => c.implemented).map((c) => c.id);

type StatusFilter = 'pending' | 'validated' | 'all';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'pending', label: 'En attente' },
  { value: 'validated', label: 'Copros validées' },
  { value: 'all', label: 'Tous les statuts' },
];

// Number of days, '' means no date restriction
const PERIOD_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Toutes les dates' },
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: 'Moins d\'un mois' },
  { value: '90', label: '3 derniers mois' },
];

const DEFAULT_STATUS_FILTER: StatusFilter = 'pending';

const filterButtonClass = (active: boolean) => `rounded-full border px-3 py-1 text-sm transition-colors ${
  active
    ? 'border-rgpd-blue-dark bg-rgpd-blue-dark text-white'
    : 'border-gray-300 bg-white text-gray-700 hover:border-rgpd-blue-dark'
}`;

const PendingConfigImports: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sortField, setSortField] = useState('configExportUploadedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const navigate = useNavigate();

  // Filters are kept in the URL so they survive a round trip to the company page
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const statusFilter: StatusFilter = STATUS_FILTERS.some(({ value }) => value === statusParam)
    ? statusParam as StatusFilter
    : DEFAULT_STATUS_FILTER;
  const periodParam = searchParams.get('uploadedWithinDays') ?? '';
  const periodFilter = PERIOD_FILTERS.some(({ value }) => value === periodParam) ? periodParam : '';
  const hasActiveFilters = statusFilter !== DEFAULT_STATUS_FILTER || periodFilter !== '';

  const limit = 20;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCompanies = useCallback(async() => {
    if (!jwt) {return;}
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        step: page.toString(),
        limit: limit.toString(),
        sortField,
        sortDirection,
        filterStatus: statusFilter,
      });
      if (debouncedSearch) {
        params.set('filterSearch', debouncedSearch);
      }
      if (periodFilter) {
        params.set('filterUploadedWithinDays', periodFilter);
      }
      const response = await fetch(`/api/admin/pending-config-imports?${params.toString()}`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const data = await response.json() as PaginationData;
      if (!data.results || !data.pagination) {
        throw new Error('Invalid response format');
      }
      setCompanies(data.results ?? []);
      setTotalPages(data.pagination?.totalPages ?? 1);
      setTotalResults(data.pagination?.totalResults ?? 0);
    } catch (err) {
      console.error('Error fetching pending config imports :', err);
      setError('Une erreur est survenue lors du chargement des données. Veuillez réessayer.');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [jwt, page, limit, debouncedSearch, sortField, sortDirection, statusFilter, periodFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleDownload = async(companyId: number, companyName: string) => {
    if (!jwt) {return;}
    setDownloadingId(companyId);
    try {
      await downloadCompanyConfigExport(companyId, companyName, jwt);
    } catch (downloadError) {
      console.error('Error downloading file :', downloadError);
      showToast('error', 'Une erreur est survenue lors du téléchargement.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFilterChange = (key: 'status' | 'uploadedWithinDays', value: string, defaultValue: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === defaultValue) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
    setPage(1);
  };

  const isSoftwareImplemented = (softwareId: string | null | undefined): boolean => {
    if (!softwareId) {return false;}
    return IMPLEMENTED_SOFTWARE.includes(softwareId as SoftwareType);
  };

  const handleImport = (company: Company) => {
    navigate(ROUTES.ADMIN_SOFTWARE_IMPORT, {
      state: {
        companyId: company.id,
        companyName: company.name,
        companySiren: company.siren,
        selectedSoftware: company.selectedSoftware,
      },
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const headers = [
    { title: 'NOM', class: 'w-1/5', sortable: true, field: 'name' },
    { title: 'SIREN', class: 'w-1/6', sortable: true, field: 'siren' },
    { title: 'LOGICIEL', class: 'w-1/6' },
    { title: 'DATE UPLOAD', class: 'w-1/6', sortable: true, field: 'configExportUploadedAt' },
    { title: 'STATUT', class: 'w-1/6' },
    { title: 'ACTIONS', class: 'w-1/6' },
  ];

  const formattedData = (companies ?? []).map((company) => ({
    NOM: (
      <button
        onClick={() => navigate(ROUTES.ADMIN_COMPANY_DETAILS.replace(':companyId', company.id.toString()))}
        className="text-left font-medium text-rgpd-blue hover:text-rgpd-blue-dark hover:underline"
        title="Ouvrir la fiche société"
      >
        {company.name}
      </button>
    ),
    SIREN: company.siren,
    LOGICIEL: (
      <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-800">
        {getSoftwareDisplayName(company.selectedSoftware)}
      </span>
    ),
    'DATE UPLOAD': company.configExportUploadedAt ? (
      <div className="flex flex-col">
        <span>{formatDateWithTime(company.configExportUploadedAt)}</span>
        <span className="text-xs text-gray-500">{formatUploadAge(company.configExportUploadedAt)}</span>
      </div>
    ) : '-',
    STATUT: isCoproValidated(company) ? (
      <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs whitespace-nowrap text-green-800">
        Copros validées
      </span>
    ) : (
      <span className="rounded-full bg-orange-100 px-2.5 py-1 text-xs whitespace-nowrap text-orange-800">
        En attente
      </span>
    ),
    ACTIONS: (
      <div className="flex flex-col gap-2">
        {isSoftwareImplemented(company.selectedSoftware) && (
          <button
            onClick={() => handleImport(company)}
            className="hover:bg-opacity-90 flex items-center gap-2 rounded bg-rgpd-green px-4 py-2 text-sm text-white shadow-md transition-all"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Importer
          </button>
        )}
        <button
          onClick={() => handleDownload(company.id, company.name)}
          disabled={downloadingId === company.id}
          className="hover:bg-opacity-90 flex items-center gap-2 rounded bg-rgpd-blue-dark px-4 py-2 text-sm text-white shadow-md transition-all disabled:opacity-50"
        >
          {downloadingId === company.id ? (
            <Spinner size="sm" />
          ) : (
            <ArrowDownTrayIcon className="h-4 w-4" />
          )}
          Télécharger
        </button>
      </div>
    ),
  }));

  return (
    <div className="rounded-lg bg-white p-6 shadow-md">
      <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
        <BuildingOffice2Icon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
        Imports de configuration en attente
      </h1>

      <p className="mb-6 text-sm text-gray-600">
        Liste des entreprises ayant téléchargé un fichier d'export. Par défaut, seules celles n'ayant pas encore validé
        l'étape de configuration des copropriétés sont affichées : utilisez les filtres ci-dessous pour retrouver un
        fichier récent quel que soit le statut de l'entreprise.
      </p>

      {/* Search & Stats */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="relative w-full md:w-96">
          <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom ou SIREN..."
            className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-transparent focus:ring-2 focus:ring-rgpd-blue-light"
          />
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-rgpd-blue-dark">{totalResults}</span>{' '}
          {hasActiveFilters ? 'fichier(s) correspondant(s)' : 'entreprise(s) en attente'}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <span className="mr-2 self-center text-sm text-gray-500">Statut :</span>
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handleFilterChange('status', value, DEFAULT_STATUS_FILTER)}
              className={filterButtonClass(statusFilter === value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="mr-2 self-center text-sm text-gray-500">Déposé :</span>
          {PERIOD_FILTERS.map(({ value, label }) => (
            <button
              key={value || 'all'}
              onClick={() => handleFilterChange('uploadedWithinDays', value, '')}
              className={filterButtonClass(periodFilter === value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Sort buttons */}
      <div className="mb-4 flex flex-wrap gap-2">
        <span className="mr-2 self-center text-sm text-gray-500">Trier par :</span>
        {[
          { field: 'configExportUploadedAt', label: 'Date' },
          { field: 'name', label: 'Nom' },
          { field: 'siren', label: 'SIREN' },
        ].map(({ field, label }) => (
          <button
            key={field}
            onClick={() => handleSort(field)}
            className={filterButtonClass(sortField === field)}
          >
            {label}
            {sortField === field && (
              <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner size="xl" />
        </div>
      ) : error ? (
        <div className="flex h-64 items-center justify-center">
          <Badge
            mode="error"
            text={error}
            size="large"
            type="solid"
          />
        </div>
      ) : !companies || companies.length === 0 ? (
        <div className="flex h-64 items-center justify-center">
          <Badge
            mode={hasActiveFilters ? 'info' : 'success'}
            text={hasActiveFilters ? 'Aucun fichier ne correspond à ces filtres.' : 'Aucune entreprise en attente d\'import.'}
            size="large"
            type="solid"
          />
        </div>
      ) : (
        <>
          <MUITable
            data={formattedData}
            headers={headers}
            tableClass="w-full"
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-600">
                Page {page} sur {totalPages}
              </span>
              <button
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PendingConfigImports;
